import { createContext, useContext } from 'react'

import type {
  FeatureFlags,
  FeatureFlagsQuery,
  FeatureFlagsQueryVariables,
} from '@features/flags.graphql'
import type { QueryResult } from '@shared/apollo'

/**
 * Type for the feature flags context value.
 * Includes all Apollo query result properties except 'data' (renamed to 'featureFlags').
 */
export type FeatureFlagsContextValue = Omit<
  QueryResult<FeatureFlagsQuery, FeatureFlagsQueryVariables>,
  'data'
> & {
  featureFlags: FeatureFlags | undefined
}

/**
 * Default empty context object.
 */
export const defaultContextValue = {
  featureFlags: {} as FeatureFlags,
  loading: false,
} as FeatureFlagsContextValue

/**
 * Context for storing feature flags with loading state.
 */
export const FeatureFlagsContext =
  createContext<FeatureFlagsContextValue | null>(null)

export const useFeatureFlagsContext = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext)
  if (context === null) {
    throw new Error(
      'useFeatureFlagsContext must be used within a FeatureFlagsProvider',
    )
  }
  return context
}

/**
 * Custom hook to access feature flags from the FeatureFlagsContext.
 * Note: This hook only returns the flags for backwards compatibility.
 * Use useFeatureFlagV2 if you need access to the loading state.
 * @throws If used outside of a FeatureFlagsProvider.
 * @returns The feature flags object.
 */
export const useFeatureFlags = (): FeatureFlags | undefined => {
  const context = useFeatureFlagsContext()
  return context.featureFlags
}
